/**
 * HUB-D — Actions caisse (PDF / CSV / force-close) — APIs existantes uniquement.
 */
import { useCallback, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { printTicket80mm } from '../../utils/ticket80mmPrint';

async function openPdfBlob(api, endpoint) {
  const response = await api.get(endpoint, { responseType: 'blob' });
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank', 'width=900,height=700');
  setTimeout(() => window.URL.revokeObjectURL(url), 8000);
}

export default function useActiviteCaisseActions({ api, onRefresh }) {
  const { tError } = useLanguage();
  const [forceClosingId, setForceClosingId] = useState(null);

  const printSession = useCallback(async (caisseId) => {
    try {
      await openPdfBlob(api, `/caisse/print/${caisseId}`);
    } catch (err) {
      alert(tError(err.response?.data?.detail) || 'Erreur impression caisse');
    }
  }, [api, tError]);

  const printMonthly = useCallback(async (type, caisseId) => {
    const variant = type === 'ouverture' ? 'ouverture' : type === 'fermeture' ? 'fermeture' : type === 'ventes' ? 'ventes' : null;
    if (!variant) {
      try {
        await openPdfBlob(api, `/caisse/print/${caisseId}`);
      } catch (err) {
        alert(tError(err.response?.data?.detail) || 'Erreur impression caisse');
      }
      return;
    }
    try {
      await printTicket80mm(api, { type: 'caisse', ref: caisseId, variant });
    } catch (err) {
      alert(tError(err.response?.data?.detail) || 'Erreur impression');
    }
  }, [api, tError]);

  const exportMonthly = useCallback(async (month, magasinId) => {
    try {
      const response = await api.get('/caisse/export', {
        params: { month, ...(magasinId ? { magasin_id: magasinId } : {}) },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `caisse_comptable_${month}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 8000);
    } catch (err) {
      alert(tError(err.response?.data?.detail) || 'Erreur export comptable');
    }
  }, [api, tError]);

  const forceClose = useCallback(async (caisseId, dateCaisse) => {
    if (!window.confirm(`Fermer de force la caisse du ${dateCaisse} ? Cette action est irréversible.`)) return;
    setForceClosingId(caisseId);
    try {
      await api.post(`/caisse/force-close/${caisseId}`);
      await onRefresh();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || 'Erreur lors de la fermeture forcée');
    } finally {
      setForceClosingId(null);
    }
  }, [api, onRefresh, tError]);

  return {
    forceClosingId,
    printSession,
    printMonthly,
    exportMonthly,
    forceClose,
  };
}
